import { useT } from '@/shared/i18n';
import { OrbitVisual, type OrbitNode } from './OrbitVisual';
import { Icon } from '@/shared/ui/icon';
import type { MouseEvent, ReactNode } from 'react';

/**
 * SignalNetwork — "The connected signal network" band (extraction §2/§5).
 * Surface-container-lowest, 2-col: copy + two mini cards, and the 6-node orbit
 * with a pulsing Lime diamond core. Mouse-follow spotlight overlay.
 * Theme-adaptive via tokens.
 */
export function SignalNetwork(): ReactNode {
  const { t } = useT();

  const nodes: OrbitNode[] = [
    { label: t('landing.dark.node.skills'), position: 'top-[15%] left-[20%]', delay: '0s' },
    { label: t('landing.dark.node.goals'), position: 'top-[35%] right-[10%]', delay: '1.5s' },
    { label: t('landing.dark.node.experience'), position: 'bottom-[25%] left-[10%]', delay: '3s' },
    { label: t('landing.dark.node.learning'), position: 'bottom-[40%] right-[15%]', delay: '0.8s' },
    { label: t('landing.dark.node.opportunities'), position: 'top-[5%] right-[30%]', delay: '2.2s' },
    { label: t('landing.dark.node.insights'), position: 'bottom-[10%] right-[40%]', delay: '1.2s' },
  ];

  const onMouseMove = (e: MouseEvent<HTMLElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mouse-x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
    e.currentTarget.style.setProperty('--mouse-y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  const cards = [
    { icon: <Icon name="network" size={26} />, title: t('landing.dark.intel.card1.title'), body: t('landing.dark.intel.card1.body') },
    { icon: <Icon name="lightbulb" size={26} />, title: t('landing.dark.intel.card2.title'), body: t('landing.dark.intel.card2.body') },
  ];

  return (
    // `data-theme="dark"` scopes the dark token set to this band, so the signal
    // network reads as a dark band in light mode too — the orbit, glass node
    // pills, and copy stay high-contrast in both global themes.
    <section
      id="intelligence"
      data-theme="dark"
      onMouseMove={onMouseMove}
      className="orbit-section relative scroll-mt-24 overflow-hidden bg-surface-container-lowest py-32"
    >
      <div aria-hidden="true" className="spotlight-overlay" />
      <div className="mx-auto grid max-w-container-max items-center gap-20 px-margin-mobile md:px-margin-desktop lg:grid-cols-2">
        <div className="relative z-10">
          <h2 className="mb-8 text-4xl font-bold leading-none tracking-tight text-on-surface md:text-5xl">
            {t('landing.dark.intel.lead')}
            <br />
            <span className="text-secondary-fixed">{t('landing.dark.intel.accent')}</span>
          </h2>
          <p className="mb-10 max-w-lg text-lg leading-relaxed text-on-surface-variant">
            {t('landing.dark.intel.body')}
          </p>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {cards.map((c) => (
              <div
                key={c.title}
                className="rounded-2xl border border-outline-variant/30 bg-surface-container p-6"
              >
                <span className="mb-4 inline-block text-secondary-fixed">{c.icon}</span>
                <h4 className="mb-2 font-semibold text-on-surface">{c.title}</h4>
                <p className="text-sm text-on-surface-variant">{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        <OrbitVisual
          heightClass="h-[560px]"
          rings={[250, 400, 550]}
          nodes={nodes}
          core={
            <div className="node-pulse z-20 flex h-20 w-20 rotate-45 items-center justify-center rounded-2xl bg-secondary-fixed shadow-node">
              <Icon name="sparkles" className="-rotate-45 text-on-secondary-fixed" size={32} />
            </div>
          }
        />
      </div>
    </section>
  );
}

export default SignalNetwork;
